import { lazy, Suspense, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useIsLoggedIn } from '@/entities/auth';
import { LanguageBox } from '@/features/change-language';
import { useMobileNavStore } from '@/features/mobile-nav';
import { buildStaticAssetUrl, deviceSizes } from '@/shared/config';

const Drawer = lazy(() => import('antd/es/drawer'));

export interface NavBarNavigationTarget {
  id: string;
  href: string;
  navMessageId?: string;
}

interface NavBarV2Props {
  homeHref: string;
  myStuffHref: string;
  navTargets: ReadonlyArray<NavBarNavigationTarget>;
  signInHref: string;
}

const NavShell = styled.header`
  background: var(--brand);
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  left: 0;
  max-height: var(--nav-height);
  padding: 0 28px;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 10;

  :where(a, button, input, select, [tabindex]):focus-visible {
    outline: 2px solid var(--white);
    outline-offset: -3px;
    box-shadow: 0 0 0 1px var(--brand);
  }

  @media screen and (max-width: ${deviceSizes.lg}) {
    max-height: none;
    padding: 0;
  }
`;

const DesktopNav = styled.div`
  align-items: stretch;
  display: flex;
  max-width: var(--container-content);
  min-width: 0;
  width: 100%;

  @media screen and (max-width: ${deviceSizes.lg}) {
    display: none;
  }
`;

const MobileNav = styled.div`
  align-items: center;
  display: none;
  flex: 1;
  justify-content: space-between;

  @media screen and (max-width: ${deviceSizes.lg}) {
    display: flex;
  }
`;

const LogoSection = styled.a`
  &:active {
    opacity: 0.8;
  }
  align-items: center;
  color: var(--white);
  display: flex;
  padding: 10px;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 30px;
  margin-right: 5px;
  width: 30px;
`;

const OrgName = styled.span`
  color: var(--white);
  font-family: var(--font-sans);
  font-size: clamp(11px, 3.4vw, var(--text-base));
  font-weight: var(--weight-black);
  text-transform: uppercase;
`;

const NavButtons = styled.nav`
  display: flex;
  flex: 1;
  gap: clamp(18px, 3vw, 72px);
  justify-content: center;
  min-width: 0;
`;

const UserSection = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 20px;

  @media screen and (max-width: ${deviceSizes.xs}) {
    padding: 0 12px;
  }
`;

const NavItem = styled.a<{ $drawerMode?: boolean }>`
  &:active {
    background-color: var(--brand-hover);
  }
  align-items: center;
  color: var(--white);
  display: flex;
  flex: ${({ $drawerMode }) => ($drawerMode ? '1 1 auto' : '0 0 auto')};
  font-size: ${({ $drawerMode }) => ($drawerMode ? '14px' : 'clamp(12px, 0.9vw, 15px)')};
  font-weight: var(--weight-semibold);
  justify-content: center;
  min-width: ${({ $drawerMode }) => ($drawerMode ? '0' : '78px')};
  min-height: ${({ $drawerMode }) => ($drawerMode ? '52px' : 'var(--nav-height)')};
  padding: ${({ $drawerMode }) => ($drawerMode ? '14px 20px' : '0 8px')};
  text-align: center;
  text-decoration: none;
  transition: background-color var(--duration-fast) ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--brand-hover);
      color: var(--white);
    }
  }

  @media screen and (max-width: ${deviceSizes.lg}) {
    font-size: ${({ $drawerMode }) => ($drawerMode ? '14px' : 'clamp(10px, 1.2vw, 14px)')};
  }
`;

const LoginButton = styled.a`
  align-items: center;
  display: inline-flex;
  min-block-size: 44px;
  min-inline-size: 44px;

  &:active {
    opacity: 0.8;
  }
  color: var(--white);
  font-size: clamp(11px, 1.4vw, 16px);
  font-weight: var(--weight-semibold);
  margin-left: 10px;
  text-decoration: none;

  @media screen and (max-width: ${deviceSizes.lg}) {
    font-size: 14px;
  }
`;

const BurgerButton = styled.button`
  min-block-size: 44px;
  min-inline-size: 44px;

  &:active {
    opacity: 0.8;
  }
  background: transparent;
  border: none;
  color: var(--white);
  cursor: pointer;
  font-size: 25px;
  padding: 0 12px;
`;

const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
`;

function NavigationLinks({
  drawerMode = false,
  navTargets,
  onNavigate,
}: {
  drawerMode?: boolean;
  navTargets: ReadonlyArray<NavBarNavigationTarget>;
  onNavigate?: () => void;
}) {
  return navTargets.flatMap((item) => {
    if (!item.navMessageId) {
      return [];
    }

    return [
      <NavItem key={item.id} $drawerMode={drawerMode} href={item.href} onClick={onNavigate}>
        <FormattedMessage id={item.navMessageId} />
      </NavItem>,
    ];
  });
}

export function NavBarV2({ homeHref, myStuffHref, navTargets, signInHref }: NavBarV2Props) {
  const intl = useIntl();
  const isLoggedIn = useIsLoggedIn();
  const mobileNavOpen = useMobileNavStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useMobileNavStore((state) => state.setMobileNavOpen);
  const [hasOpenedMobileNav, setHasOpenedMobileNav] = useState(false);
  const logoUrl = buildStaticAssetUrl('images', intl.formatMessage({ id: 'logo.white' }));

  return (
    <>
      <NavShell>
        <DesktopNav>
          <LogoSection aria-label="CreatiCode home" href={homeHref}>
            <LogoImage alt="CreatiCode logo" src={logoUrl} />
            <OrgName>
              <FormattedMessage id="TopBanner.creaticode" />
            </OrgName>
          </LogoSection>
          <NavButtons>
            <NavigationLinks navTargets={navTargets} />
          </NavButtons>
          <UserSection>
            <LanguageBox />
            <LoginButton href={isLoggedIn ? myStuffHref : signInHref}>
              <FormattedMessage id={isLoggedIn ? 'TopBanner.myStuff' : 'TopBanner.signIn'} />
            </LoginButton>
          </UserSection>
        </DesktopNav>

        <MobileNav>
          <BurgerButton
            aria-label="Open navigation menu"
            onClick={() => {
              setHasOpenedMobileNav(true);
              setMobileNavOpen(true);
            }}
            type="button"
          >
            <FontAwesomeIcon icon={faBars} />
          </BurgerButton>
          <LogoSection aria-label="CreatiCode home" href={homeHref}>
            <LogoImage alt="CreatiCode logo" src={logoUrl} />
            <OrgName>
              <FormattedMessage id="TopBanner.creaticode" />
            </OrgName>
          </LogoSection>
          <UserSection>
            <LanguageBox />
            <LoginButton href={isLoggedIn ? myStuffHref : signInHref}>
              <FormattedMessage id={isLoggedIn ? 'TopBanner.myStuff' : 'TopBanner.signIn'} />
            </LoginButton>
          </UserSection>
        </MobileNav>
      </NavShell>

      {mobileNavOpen || hasOpenedMobileNav ? (
        <Suspense fallback={null}>
          <Drawer
            closeIcon={null}
            onClose={() => setMobileNavOpen(false)}
            open={mobileNavOpen}
            placement="left"
            styles={{
              body: { background: 'var(--brand)', padding: 0 },
              header: { background: 'var(--brand)', border: 0, color: 'var(--white)' },
            }}
            title={<FormattedMessage id="TopBanner.menu" />}
          >
            <DrawerNav>
              <NavigationLinks drawerMode navTargets={navTargets} onNavigate={() => setMobileNavOpen(false)} />
            </DrawerNav>
          </Drawer>
        </Suspense>
      ) : null}
    </>
  );
}
